<div class="min-h-screen bg-slate-50 flex justify-center sm:pb-20">
  <div
    class="w-full max-w-md bg-white min-h-screen sm:min-h-[calc(100vh-2rem)] sm:mt-8 sm:rounded-3xl sm:shadow-2xl sm:border border-slate-200 overflow-hidden relative flex flex-col">

    <!-- Header -->
    <div class="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-100 shadow-sm">
      <div class="flex items-center gap-4 px-5 py-4">
        <button
          class="h-10 w-10 grid place-items-center rounded-2xl hover:bg-slate-50 active:scale-95 transition-all text-slate-700"
          (click)="toBack()">
          <span class="material-symbols-outlined text-slate-700">arrow_back</span>
        </button>
        <div class="flex-1">
          <h1 class="text-xl font-black text-slate-800 tracking-tight">{{ 'อัปโหลดหลักฐาน' | translate : 'upload-payment' }}</h1>
          <p class="text-xs font-medium text-slate-500 mt-0.5">{{ 'แนบสลิปการโอนเงินเพื่อยืนยัน' | translate : 'upload-payment' }}</p>
        </div>
      </div>
    </div>

    <!-- Hidden file input -->
    <input id="slipFileInput" type="file" accept="image/*" class="hidden" (change)="onFileSelect($event)" />

    <div class="flex-1 overflow-y-auto w-full px-5 pt-5 pb-32 space-y-4">
      <!-- Section: หลักฐานการชำระเงิน -->
      <div class="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
        <div class="p-5">

          <!-- Section Header -->
          <div class="flex items-center gap-3 mb-4">
            <div class="w-8 h-8 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
              <span class="material-symbols-outlined text-green-500 text-[18px]">receipt_long</span>
            </div>
            <div class="text-sm font-bold text-slate-800">{{ 'สลิปการโอนเงิน' | translate : 'upload-payment' }}</div>
          </div>

          <!-- Upload Zone -->
          @if (previewUrl()) {
          <div class="relative rounded-2xl overflow-hidden border border-slate-100 bg-slate-50 shadow-inner">
            <img [src]="previewUrl()" [alt]="'สลิปการโอนเงิน' | translate : 'upload-payment'" class="w-full max-h-80 object-contain" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none"></div>
            <button
              class="absolute top-3 right-3 h-8 w-8 rounded-full bg-red-500/90 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-red-600 active:scale-95 transition-all"
              (click)="removeFile()">
              <span class="material-symbols-outlined text-white text-[16px]">close</span>
            </button>
            <div class="absolute bottom-4 left-1/2 -translate-x-1/2">
              <button
                class="flex items-center gap-2 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-black/20 hover:bg-white/30 active:scale-95 transition-all"
                (click)="triggerFileInput()">
                <span class="material-symbols-outlined text-[18px]">swap_horiz</span>
                {{ 'เปลี่ยนรูปภาพ' | translate : 'upload-payment' }}
              </button>
            </div>
          </div>
          } @else {
          <div
            class="rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50/50 flex flex-col items-center justify-center gap-3 min-h-[180px] cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all group"
            (click)="triggerFileInput()">
            <div
              class="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center group-hover:scale-105 transition-transform group-hover:shadow-md">
              <span
                class="material-symbols-outlined text-slate-400 text-[32px] group-hover:text-blue-500 transition-colors">upload_file</span>
            </div>
            <div class="text-center px-4">
              <div class="text-[15px] font-bold text-slate-700 group-hover:text-blue-600 transition-colors">
                {{ 'แตะเพื่อเลือกรูปสลิป' | translate : 'upload-payment' }}</div>
              <div class="text-xs text-slate-400 mt-1">{{ 'รองรับ JPG, PNG ขนาดไม่เกิน 1 MB' | translate : 'upload-payment' }}</div>
            </div>
          </div>
          }

          @if (fileSizeError()) {
          <div
            class="mt-3 flex items-start gap-1.5 text-xs text-red-500 bg-red-50 p-2.5 rounded-xl border border-red-100">
            <span class="material-symbols-outlined text-[16px] shrink-0 mt-0.5">error</span>
            <span class="leading-relaxed">{{ fileSizeError() }}</span>
          </div>
          }

          <!-- Select File Button (always visible) -->
          @if (!previewUrl()) {
          <button
            class="mt-4 w-full flex items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 py-3.5 text-sm font-bold text-blue-700 hover:bg-blue-100 active:scale-[0.98] transition-all"
            (click)="triggerFileInput()">
            <span class="material-symbols-outlined text-[20px]">upload</span>
            {{ 'เลือกไฟล์จากอุปกรณ์' | translate : 'upload-payment' }}
          </button>
          }
        </div>
      </div>

      <!-- Section: รายละเอียดการโอน -->
      <div class="bg-white rounded-3xl border border-slate-100 shadow-sm relative overflow-visible z-10">
        <div class="p-5">

          <!-- Section Header -->
          <div class="flex items-center gap-3 mb-5">
            <div class="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <span class="material-symbols-outlined text-blue-500 text-[18px]">schedule</span>
            </div>
            <div class="text-sm font-bold text-slate-800">{{ 'รายละเอียดช่องทางการโอน' | translate : 'upload-payment' }}</div>
          </div>

          <div class="space-y-4">
            <!-- Date & Time -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {{ 'วันที่โอนเงิน' | translate : 'upload-payment' }}<span class="text-red-400 ml-0.5">*</span>
                </label>
                <div class="rounded-2xl overflow-hidden [&_.p-datepicker]:w-full">
                  <p-datepicker [(ngModel)]="transferDate" [showIcon]="true" iconDisplay="input" dateFormat="dd/mm/yy"
                    [placeholder]="'เลือกวันที่' | translate : 'upload-payment'" [maxDate]="today" appendTo="body" class="w-full"
                    inputStyleClass="w-full text-[15px] font-medium rounded-2xl border-slate-200 bg-slate-50 focus:ring-blue-500 focus:border-blue-500" />
                </div>
              </div>
              <div>
                <label class="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {{ 'เวลาที่โอนเงิน' | translate : 'upload-payment' }}<span class="text-red-400 ml-0.5">*</span>
                </label>
                <div class="rounded-2xl overflow-hidden [&_.p-datepicker]:w-full">
                  <p-datepicker #timePicker [(ngModel)]="transferTime" [timeOnly]="true" [showIcon]="true"
                    iconDisplay="input" [placeholder]="'เลือกเวลา' | translate : 'upload-payment'" appendTo="body" class="w-full"
                    inputStyleClass="w-full text-[15px] font-medium rounded-2xl border-slate-200 bg-slate-50 focus:ring-blue-500 focus:border-blue-500">
                    <ng-template pTemplate="footer">
                      <div class="px-3 pb-3 pt-1">
                        <button type="button"
                          class="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold py-2.5 active:scale-95 transition-all"
                          (click)="closeTimePicker()">
                          {{ 'ยืนยันเวลา' | translate : 'upload-payment' }}
                        </button>
                      </div>
                    </ng-template>
                  </p-datepicker>
                </div>
              </div>
            </div>

            <!-- Amount -->
            <div>
              <label class="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                {{ 'จำนวนเงินโอนสุทธิ' | translate : 'upload-payment' }}<span class="text-red-400 ml-0.5">*</span>
              </label>
              <div class="relative group">
                <span
                  class="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[20px] text-slate-400 group-focus-within:text-blue-500 transition-colors">paid</span>
                <input type="number" [(ngModel)]="amount" min="0" placeholder="0.00"
                  class="w-full rounded-2xl border border-slate-200 bg-slate-50 pl-12 pr-14 py-3.5 text-lg font-bold text-slate-800 placeholder:text-slate-300 placeholder:font-normal focus:bg-white focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-right" />
                <span class="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">THB</span>
              </div>
            </div>

          </div>
        </div>
      </div>

    </div>
    <br><br><br>
    <!-- Bottom Bar (sticky) -->
    <div
      class="absolute bottom-0 inset-x-0 bg-white/80 backdrop-blur-md border-t border-slate-100 p-5 z-20 shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
      <!-- Error -->
      @if (submitError()) {
      <div
        class="mb-3 rounded-2xl bg-red-50 border border-red-200 p-3 flex items-start gap-2.5 text-xs text-red-600 shadow-sm">
        <span class="material-symbols-outlined text-[18px] text-red-500 shrink-0 mt-0.5">error</span>
        <span class="leading-relaxed font-medium">{{ submitError() | translate : 'api' }}</span>
      </div>
      }
      <button
        class="w-full rounded-2xl bg-gradient-to-r py-4 text-sm font-bold text-white transition-all flex items-center justify-center gap-2"
        [ngClass]="submitLoading() ? 'from-slate-400 to-slate-500 cursor-not-allowed opacity-80' : 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] shadow-lg shadow-blue-500/30'"
        [disabled]="submitLoading()" (click)="submit()">
        @if (submitLoading()) {
        <span class="material-symbols-outlined text-[20px] animate-spin">progress_activity</span>
        {{ 'กำลังตรวจสอบและอัปโหลด...' | translate : 'upload-payment' }}
        } @else {
        {{ 'ยืนยันส่งหลักฐานการชำระเงิน' | translate : 'upload-payment' }}
        <span class="material-symbols-outlined text-[20px]">send</span>
        }
      </button>
    </div>

  </div>
</div>
